import { getStaticData, isSameAccount } from "@/game-lol/utils/util.mjs";

// const MIN_SKIRMISH_SIZE = 4;
const MIN_TEAMFIGHT_SIZE = 6;

// Coordinates
const DRAGON_X_POS = 9850;
const DRAGON_Y_POS = 4200;
const BARON_X_POS = 4750;
const BARON_Y_POS = 10300;
const EPIC_MONSTER_RADIUS = 2750;

export function matchStateKey(region = "", matchId = 0) {
  return `${region.toUpperCase()}_${matchId}`;
}

export function matchStatePath(region = "", matchId = 0, id = null) {
  if (id) return ["lol", "matches", id];
  return ["lol", "matches", matchStateKey(region, matchId)];
}

export function findPlayer(profile, match) {
  if (!profile || !match?.participants) {
    // devError("findPlayer called without profile or match");
    return null;
  }

  if (profile.riotAccount?.puuid) {
    const puuidMatch = match.participants.find(
      (participant) =>
        participant.puuid && participant.puuid === profile.riotAccount?.puuid,
    );
    if (puuidMatch) return puuidMatch;
  }

  for (const participant of match.participants) {
    const { isLocalPlayer, riotIdGameName, riotIdTagline } = participant;

    // this just overrides everything if it comes from LCU? not sure if correct
    if (isLocalPlayer) return participant;

    // Check current Riot ID
    if (
      riotIdGameName === profile.riotAccount?.gameName &&
      riotIdTagline === profile.riotAccount?.tagLine
    )
      return participant;
  }

  for (const participant of match.participants) {
    if (isSameAccount(participant, profile)) return participant;
  }

  return null;
}

/**
 * Compare 2 match participants to see if they are the same player.
 * @param {matchParticipant} player1
 * @param {matchParticipant} player2
 * @returns
 */
export function isSamePlayer(player1 = {}, player2 = {}) {
  const matchesParticipantId =
    player1.participantId && player1.participantId === player2.participantId;
  if (matchesParticipantId) return true;

  const matchesGameName =
    player1.riotIdGameName &&
    player2.riotIdGameName &&
    player1.riotIdGameName === player2.riotIdGameName;
  const matchesTagLine =
    player1.riotIdTagline &&
    player2.riotIdTagline &&
    player1.riotIdTagline === player2.riotIdTagline;
  const matchedRiotId = matchesGameName && matchesTagLine;
  if (matchedRiotId) return true;

  const matchesPuuid =
    player1.puuid && player2.puuid && player1.puuid === player2.puuid;
  if (matchesPuuid) return true;

  const matchesSummonerName =
    player1.summonerName &&
    player2.summonerName &&
    player1.summonerName === player2.summonerName;
  if (matchesSummonerName) return true;

  const matchesDamage = player1.totalDamageDealt === player2.totalDamageDealt;
  if (matchesDamage) return true;

  if (!(player1.riotIdGameName || player1.riotIdTagline || player1.puuid)) {
    return player1.championId === player2.championId;
  }
  return false;
}

export function calculateAvgMinionValue(timestamp) {
  const mins = timestamp / 60000;

  if (mins <= 15) return 19.8;
  else if (mins <= 17.25) return 22.6;
  else if (mins <= 25) return 23;
  return 27.6;
}

/**
 * Determine the timestamp (ms) of when mid-game ended and end-game began.
 * Based on average player gold spent.
 */
export function calcStartOfEndGame(timeline) {
  const ENDGAME_GOLD_BREAKPOINT = 13000;

  let lateGameStartTimestamp = 0;
  let lobbyInventoryValue = 0;

  const itemsStaticData = getStaticData("items");

  const { frames = [], participants = [] } = timeline || {};

  if (!frames.length || !Object.keys(itemsStaticData).length)
    return lateGameStartTimestamp;

  framesLoop: for (const frame of frames) {
    for (const event of frame.events || []) {
      if (event.type === "ITEM_PURCHASED") {
        const itemValue = itemsStaticData?.[event.itemId]?.gold?.total ?? 0;
        lobbyInventoryValue += itemValue;
        const avgPlayerValue = lobbyInventoryValue / participants.length;
        if (avgPlayerValue >= ENDGAME_GOLD_BREAKPOINT) {
          lateGameStartTimestamp = event.timestamp;
          break framesLoop;
        }
      }
    }
  }

  return lateGameStartTimestamp;
}

function inSeconds(timestamp) {
  return timestamp / 1000;
}
function withinTimeRange(time1, time2, diff) {
  const timeUpper = time2 + diff;
  const timeLower = time2 - diff;

  return time1 <= timeUpper && time1 >= timeLower;
}
function isNearby(pos1, pos2, r = 3000) {
  const distance = Math.sqrt(
    Math.pow(pos2.x - pos1.x, 2) + Math.pow(pos2.y - pos1.y, 2),
  );
  return distance <= r;
}
function isDragonFight(fightPos) {
  return isNearby(
    fightPos,
    { x: DRAGON_X_POS, y: DRAGON_Y_POS },
    EPIC_MONSTER_RADIUS,
  );
}
function isBaronFight(fightPos) {
  return isNearby(
    fightPos,
    { x: BARON_X_POS, y: BARON_Y_POS },
    EPIC_MONSTER_RADIUS,
  );
}

export function teamfightEvents(events = [], fightSize = MIN_TEAMFIGHT_SIZE) {
  let teamfight = null;

  const killsThisMin = events.filter((event) => event.type === "CHAMPION_KILL");

  for (const mainKill of killsThisMin) {
    const { assistingParticipantIds = [], killerId, victimId } = mainKill;
    if (assistingParticipantIds.length < 3) continue; // "teamfight"/skirmish took place

    const involvedInMainKill = [...assistingParticipantIds, killerId, victimId];

    for (const otherKill of killsThisMin) {
      const { assistingParticipantIds = [], killerId, victimId } = otherKill;

      const nearby = isNearby(otherKill.position, mainKill.position); // Within range of the fight defining kill?
      const recently = withinTimeRange(
        inSeconds(otherKill.timestamp),
        inSeconds(mainKill.timestamp),
        15,
      ); // Within 15s of the fight defining kill?

      if (!recently || !nearby) continue;

      const involvedInKill = [...assistingParticipantIds, killerId, victimId];

      for (const player of involvedInKill) {
        // Involve everyone some the teriary kills, with the main fight
        if (!involvedInMainKill.includes(player)) {
          involvedInMainKill.push(player);
        }
      }
    }

    if (involvedInMainKill.length >= fightSize) {
      teamfight = {
        position: mainKill.position,
        isNearDragonPit: isDragonFight(mainKill.position),
        isNearBaronPit: isBaronFight(mainKill.position),
        timestamp: mainKill.timestamp,
        involved: involvedInMainKill,
      };
    }
  }

  return teamfight;
}
